import { useState, useEffect, useRef } from 'react';
import { getRankImage, getRoleImage } from '../common/consts';
import useStore from '../store/store';
import './Teams.css';

function Teams() {
  const [seasons, setSeasons] = useState([]);
  const [seasonTeams, setSeasonTeams] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const originalBodyClass = useRef('');
  const seasonTabsRef = useRef(null);
  const teamLogosRef = useRef(null);

  useEffect(() => {
    // Store original body class and add no-bg class when Teams component mounts
    const bodyElement = document.querySelector('body');
    originalBodyClass.current = bodyElement.className;
    bodyElement.className = 'no-bg';

    // Restore original body class when component unmounts
    return () => {
      bodyElement.className = originalBodyClass.current;
    };
  }, []);

  useEffect(() => {
    // Subscribe to store changes for real-time updates
    const unsubscribe = useStore.subscribe((state) => {
      // Handle errors
      if (state.errors) {
        if (state.errors.seasons) {
          setError(`Failed to load seasons: ${state.errors.seasons}`);
          setLoading(false);
          return;
        }
        if (state.errors.seasonTeams) {
          setError(`Failed to load teams: ${state.errors.seasonTeams}`);
          setLoading(false);
          return;
        }
      }

      if (state.seasons && state.seasons.length > 0) {
        setSeasons(state.seasons);
        setError(null);

        // Use the current season from store or fallback to first season
        const currentSeasonId = state.currentSeason?.id || state.seasons[0]?.id;
        setActiveSeason(currentSeasonId);

        // Load teams for the current season
        if (state.seasonTeams && currentSeasonId) {
          const teamsForSeason = state.seasonTeams[currentSeasonId] || [];
          setSeasonTeams(teamsForSeason);
          // Set the first team as selected if there are teams
          if (teamsForSeason.length > 0) {
            setSelectedTeam(teamsForSeason[0]?.id);
          }
        }

        setLoading(false);
      }
    });

    // Trigger fetchSeasonTeams if not already loaded
    const storeState = useStore.getState();
    if (!storeState.seasonTeams || Object.keys(storeState.seasonTeams).length === 0) {
      storeState.fetchSeasonTeams();
    }

    // Initialize with current store state
    const currentState = useStore.getState();
    if (currentState.seasons && currentState.seasons.length > 0) {
      setSeasons(currentState.seasons);
      setError(null);
      const currentSeasonId = currentState.currentSeason?.id || currentState.seasons[0]?.id;
      setActiveSeason(currentSeasonId);

      if (currentState.seasonTeams && currentSeasonId) {
        const teamsForSeason = currentState.seasonTeams[currentSeasonId] || [];
        setSeasonTeams(teamsForSeason);
        if (teamsForSeason.length > 0) {
          setSelectedTeam(teamsForSeason[0]?.id);
        }
      }

      setLoading(false);
    } else if (!currentState.loading?.seasons && !currentState.loading?.seasonTeams) {
      // Data has finished loading but no seasons found
      setLoading(false);
    }

    return unsubscribe;
  }, []);

  // Handle season change to update teams
  useEffect(() => {
    if (!activeSeason) return;

    const state = useStore.getState();

    if (state.seasonTeams && state.seasonTeams[activeSeason]) {
      const teamsForSeason = state.seasonTeams[activeSeason] || [];
      setSeasonTeams(teamsForSeason);
      // Set the first team as selected if there are teams
      if (teamsForSeason.length > 0) {
        setSelectedTeam(teamsForSeason[0]?.id);
      } else {
        setSelectedTeam(null);
      }
    }
  }, [activeSeason]);

  // Get current team's players
  const currentTeam = seasonTeams.find((team) => team.id === selectedTeam);
  const teamPlayers = currentTeam?.players || [];

  // Helper function to determine season status
  const getSeasonStatus = (season) => {
    if (!season || !season.start_date || !season.end_date) return 'unknown';

    const now = new Date();
    const startDate = new Date(season.start_date);
    const endDate = new Date(season.end_date);

    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'completed';
    return 'active';
  };

  // Get current season object for status checking
  const currentSeasonObj = seasons.find((season) => season.id === activeSeason);
  const seasonStatus = getSeasonStatus(currentSeasonObj);

  // Scroll function for horizontal scrolling
  const scroll = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 300;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="teams-container">
        <div className="season-status-message">
          <h2>Loading...</h2>
          <p>Loading teams data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="teams-container">
        <div className="season-status-message">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Show no seasons state
  if (seasons.length === 0) {
    return (
      <div className="teams-container">
        <div className="season-status-message">
          <h2>No Seasons Available</h2>
          <p>There are no seasons configured yet. Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="teams-container">
      <div className="season-tabs-wrapper">
        <button className="scroll-arrow left" onClick={() => scroll(seasonTabsRef, 'left')} aria-label="Scroll left">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <div className="season-tabs" ref={seasonTabsRef}>
          {seasons.map((season) => (
            <div
              key={season.id}
              className={`season-tab ${activeSeason === season.id ? 'active' : ''}`}
              onClick={() => {
                setActiveSeason(season.id);
                // Update the store's current season
                useStore.getState().setCurrentSeason(season);
              }}
            >
              <span className="season-name">{season.name}</span>
            </div>
          ))}
        </div>
        <button className="scroll-arrow right" onClick={() => scroll(seasonTabsRef, 'right')} aria-label="Scroll right">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>

      <div className="team-logos-wrapper">
        <button className="scroll-arrow left" onClick={() => scroll(teamLogosRef, 'left')} aria-label="Scroll left">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <div className="team-logos-container">
          <div className="team-logos-scroll" ref={teamLogosRef}>
            {seasonTeams.map((team) => (
              <div
                key={team.id}
                className={`team-logo ${selectedTeam === team.id ? 'selected' : ''}`}
                onClick={() => setSelectedTeam(team.id)}
              >
                {team.logo && team.logo.trim() !== '' ? (
                  <img src={team.logo} alt={team.name || 'Team Logo'} />
                ) : (
                  <div className="team-logo-placeholder">{team.name ? team.name.charAt(0).toUpperCase() : 'T'}</div>
                )}
              </div>
            ))}
          </div>
        </div>
        <button className="scroll-arrow right" onClick={() => scroll(teamLogosRef, 'right')} aria-label="Scroll right">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>

      {/* Conditional content based on season status and team availability */}
      {seasonStatus === 'upcoming' ? (
        <div className="season-status-message">
          <h2>Upcoming Season</h2>
          <p>This season hasn't started yet. Stay tuned for team announcements!</p>
        </div>
      ) : seasonTeams.length === 0 ? (
        <div className="season-status-message">
          <h2> This season's details will be available soon</h2>
        </div>
      ) : selectedTeam && teamPlayers.length > 0 ? (
        <div className="teamplayer-cards-container">
          {/* Display all players in a responsive grid */}
          {Array.from({ length: Math.ceil(teamPlayers.length / 3) }, (_, rowIndex) => (
            <div key={`row-${rowIndex}`} className="player-cards-row">
              {teamPlayers.slice(rowIndex * 3, (rowIndex + 1) * 3).map((player, playerIndex) => {
                const primaryRole = getRoleImage(player.prim_role);
                const secondaryRole = getRoleImage(player.sec_role);
                const roles = [primaryRole, secondaryRole].filter(Boolean);

                return (
                  <div key={`${player.id}-${rowIndex}-${playerIndex}`} className="player-card">
                    <div className="player-info">
                      <div
                        className={`player-name ${player.tracker ? 'clickable' : ''}`}
                        onClick={() => {
                          if (player.tracker) {
                            window.open(player.tracker, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        style={{
                          cursor: player.tracker ? 'pointer' : 'default',
                        }}
                      >
                        {player.game_name && player.game_name.trim() !== ''
                          ? player.game_name
                          : player.name || player.valo_id.split('#')[0]}
                      </div>
                      <div className="player-details">
                        <div className="player-rank-label">Rank</div>
                        <div className="player-rank">
                          <img src={getRankImage(player.peak_rank)} alt={player.peak_rank} />
                        </div>
                        {roles.length > 0 && (
                          <>
                            <div className="player-roles-label">Roles</div>
                            <div className="player-roles">
                              {roles.map((role, index) => (
                                <img key={index} src={role} alt={`Role ${index + 1}`} />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="player-avatar">
                      {player.photo && player.photo.trim() !== '' && player.verified ? (
                        <img
                          src={player.photo}
                          alt={
                            player.game_name && player.game_name.trim() !== ''
                              ? player.game_name
                              : player.name || player.valo_id.split('#')[0]
                          }
                        />
                      ) : (
                        <img
                          src="/images/player_dummy.png"
                          alt={
                            player.game_name && player.game_name.trim() !== ''
                              ? player.game_name
                              : player.name || player.valo_id.split('#')[0]
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : selectedTeam ? (
        <div className="season-status-message">
          <h2>This season's details will be available soon</h2>
          <p>This team doesn't have any players assigned yet.</p>
        </div>
      ) : null}
    </div>
  );
}

export default Teams;